'use client';

import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Checkbox } from '@repo/design-system/components/ui/checkbox';
import { Modal, type ModalProps } from '@repo/design-system/components/ui/modal';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { type MapFilterShareState, SHARE_STATE_SCHEMA_VERSION } from '@repo/map-user-data/share-state';
import { css, cx } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import type { FilterState } from '@repo/store/maps/slices/mapData';
import { selectCurrentMapInfo, selectFilterState, selectGroupedCategories, selectMapConfig, selectMapData, selectMapName, selectTitleName } from '@repo/store/maps/slices/mapData';
import { createPublicMapUrl } from '@repo/types/maps/map-utils';
import { copyTextToClipboard } from '@repo/utility/clipboard';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MapEmbedModal } from '../map-embed-modal/map-embed-modal';
import { createShareState, createShareStateUrl } from '../utils/share-state-client';
import { appendMapFilterUrlParams } from '../utils/share-url-filters';
import { DiscordIcon, RedditIcon, XIcon } from '../utils/social-icons';
import { useMapShareHandlers } from '../utils/use-map-share-handlers';
interface MapShareModalProps extends ModalProps {
  /** When provided, overrides the Redux-derived share URL */
  shareUrl?: string;
  /** Hide the embed button (e.g. on checklist pages) */
  hideEmbed?: boolean;
  /** Hide the filter state checkbox (e.g. on checklist pages where no map filters exist) */
  hideFilterState?: boolean;
}
function hasShareableFilterState(filterState: FilterState): boolean {
  return filterState.hiddenSubcategories.length > 0 || filterState.hiddenRegions.length > 0 || filterState.regionFilterMode === 'include' || filterState.hideAll || filterState.teleportOnly || filterState.unfoundOnly || filterState.searchTerm.length > 0;
}
export const MapShareModal = ({
  isOpen,
  onOpenChange,
  shareUrl: externalShareUrl,
  hideEmbed,
  hideFilterState
}: MapShareModalProps) => {
  const mapName = useAppSelector(selectMapName);
  const gameTitle = useAppSelector(selectTitleName);
  const mapConfig = useAppSelector(selectMapConfig);
  const currentMapInfo = useAppSelector(selectCurrentMapInfo);
  const filterState = useAppSelector(selectFilterState);
  const groupedCategories = useAppSelector(selectGroupedCategories);
  const mapData = useAppSelector(selectMapData);
  const hostType = useHostType();
  const [isCopied, setIsCopied] = useState(false);
  const [includeFilterState, setIncludeFilterState] = useState(true);
  const {
    t
  } = useT(['game_map', 'common']);
  const analytics = useAnalytics();
  const [isEmbedModalOpen, setEmbedModalOpen] = useState(false);
  const [shortShareUrl, setShortShareUrl] = useState<string | null>(null);
  const [shortShareUrlFailed, setShortShareUrlFailed] = useState(false);
  const getBaseShareUrl = useCallback(() => externalShareUrl ?? (mapConfig?.titleSlug && currentMapInfo?.slug ? createPublicMapUrl({
    titleSlug: mapConfig.titleSlug,
    mapSlug: currentMapInfo.slug
  }) : ''), [currentMapInfo?.slug, externalShareUrl, mapConfig?.titleSlug]);
  const shareStatePayload = useMemo<MapFilterShareState | null>(() => {
    if (!includeFilterState || hideFilterState || !mapConfig?.externalId || !hasShareableFilterState(filterState)) {
      return null;
    }
    return {
      version: SHARE_STATE_SCHEMA_VERSION,
      kind: 'mapFilter',
      mapExternalId: mapConfig.externalId,
      filters: {
        hiddenSubcategories: filterState.hiddenSubcategories,
        hiddenRegions: filterState.hiddenRegions,
        regionFilterMode: filterState.regionFilterMode,
        hideAll: filterState.hideAll,
        teleportOnly: filterState.teleportOnly,
        unfoundOnly: filterState.unfoundOnly,
        searchTerm: filterState.searchTerm
      }
    };
  }, [filterState, hideFilterState, includeFilterState, mapConfig?.externalId]);
  const createShortShareUrl = useCallback(async () => {
    const baseUrl = getBaseShareUrl();
    if (!baseUrl || !shareStatePayload) return null;
    const token = await createShareState(shareStatePayload);
    return createShareStateUrl(baseUrl, token);
  }, [getBaseShareUrl, shareStatePayload]);
  useEffect(() => {
    setShortShareUrl(null);
    setShortShareUrlFailed(false);
    if (!isOpen || !shareStatePayload) return;
    let cancelled = false;
    createShortShareUrl().then(url => {
      if (!cancelled && url) {
        setShortShareUrl(url);
      }
    }).catch(() => {
      if (!cancelled) {
        setShortShareUrlFailed(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [createShortShareUrl, isOpen, shareStatePayload]);
  const createFallbackFilteredShareUrl = useCallback((baseUrl_0: string) => {
    const url_0 = new URL(baseUrl_0);
    appendMapFilterUrlParams(url_0, {
      filterState,
      groupedCategories,
      mapData
    });
    return url_0.toString();
  }, [filterState, groupedCategories, mapData]);
  const getShareUrl = useCallback((hideFilters?: boolean) => {
    const baseUrl_1 = getBaseShareUrl();
    if (!baseUrl_1) {
      return '';
    }
    if (!includeFilterState || hideFilters || hideFilterState) {
      return baseUrl_1;
    }
    if (shortShareUrl) {
      return shortShareUrl;
    }
    if (shareStatePayload) {
      if (shortShareUrlFailed) {
        return createFallbackFilteredShareUrl(baseUrl_1);
      }
      return baseUrl_1;
    }
    return createFallbackFilteredShareUrl(baseUrl_1);
  }, [createFallbackFilteredShareUrl, getBaseShareUrl, hideFilterState, includeFilterState, shareStatePayload, shortShareUrlFailed, shortShareUrl]);
  const getShareUrlForAction = useCallback(async (hideFilters_0?: boolean) => {
    let shareUrl = getShareUrl(hideFilters_0);
    if (shareStatePayload && !shortShareUrl && !hideFilters_0 && !hideFilterState) {
      try {
        const generatedShareUrl = await createShortShareUrl();
        if (generatedShareUrl) {
          shareUrl = generatedShareUrl;
          setShortShareUrl(generatedShareUrl);
        }
      } catch {
        const baseUrl_2 = getBaseShareUrl();
        if (baseUrl_2) {
          shareUrl = createFallbackFilteredShareUrl(baseUrl_2);
        }
        setShortShareUrlFailed(true);
      }
    }
    return shareUrl;
  }, [createFallbackFilteredShareUrl, createShortShareUrl, getBaseShareUrl, getShareUrl, hideFilterState, shareStatePayload, shortShareUrl]);
  const {
    handleRedditShareClick,
    handleDiscordShareClick,
    handleShareXClick
  } = useMapShareHandlers({
    mapName: mapName || '',
    gameTitle: gameTitle || '',
    titleSlug: mapConfig?.titleSlug || '',
    mapSlug: currentMapInfo?.slug || '',
    shareUrl: getShareUrl(hideFilterState),
    resolveShareUrl: () => getShareUrlForAction(hideFilterState)
  });
  const handleCopyClick = async () => {
    const shareUrl_0 = await getShareUrlForAction();
    const result = await copyTextToClipboard(shareUrl_0);
    if (result.success) {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
      toast({
        description: t($ => $.share_link_copied_to_clipboard, {
          ns: 'common'
        }),
        variant: 'success'
      });
      analytics.event('game_map_share_modal_share_link_click', {
        mapName: mapName || '',
        titleName: gameTitle || '',
        platform: hostType
      });
    } else {
      toast({
        description: t($_0 => $_0.share_map_modal.share_link_copy_error),
        variant: 'error'
      });
    }
  };
  useEffect(() => {
    if (isOpen && analytics && currentMapInfo?.externalId && hostType) {
      analytics.event('game_map_share_modal_open', {
        mapId: currentMapInfo.externalId,
        platform: hostType
      });
    }
  }, [analytics, currentMapInfo?.externalId, hostType, isOpen]);
  return <>
            <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="md" title={t($_1 => $_1.share_map_modal.share_map_title, {
      mapName: mapName,
      titleName: gameTitle
    })} className={cx(css({
      padding: '32'
    }))}>
                <FlexColumn gap="16">
                    <FlexRow align="center" gap="12">
                        <Type variant="body-m" color="white">
                            {t($_2 => $_2.share_map_modal.share_map_description, {
              mapName: mapName,
              titleName: gameTitle
            })}
                        </Type>
                    </FlexRow>

                    <FlexRow justify="center" gap="8" align="center" width="full">
                        <IconButton onClick={handleRedditShareClick} color="secondary" size="lg" aria-label={t($_3 => $_3.reddit)} className={css({
            flex: '1'
          })}>
                            <RedditIcon />
                        </IconButton>
                        <IconButton onClick={handleDiscordShareClick} color="secondary" size="lg" aria-label={t($_4 => $_4.discord)} className={css({
            flex: '1'
          })}>
                            <DiscordIcon />
                        </IconButton>
                        <IconButton onClick={handleShareXClick} color="secondary" size="lg" aria-label={t($_5 => $_5.x)} className={css({
            flex: '1'
          })}>
                            <XIcon />
                        </IconButton>
                        {!hideEmbed && <Button onClick={() => setEmbedModalOpen(true)} color="secondary" size="lg" aria-label={t($_6 => $_6.share_map_modal.embed_map)} className={css({
            flex: '1'
          })}>
                                <Icon icon="code" decorative />
                                {t($_7 => $_7.share_map_modal.embed_map)}
                            </Button>}
                    </FlexRow>

                    <FlexColumn gap="12" width="full">
                        <FlexRow gap="8" align="center" width="full">
                            <TextInput value={getShareUrl()} placeholder="" readOnly fullWidth variant="dark" size="md" />
                            <Button onClick={handleCopyClick} color="primary" size="md" aria-label={isCopied ? t($_8 => $_8.location_copied) : t($_9 => $_9.copy)}>
                                {isCopied ? <>
                                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
                                        </svg>
                                        {t($_10 => $_10.location_copied)}
                                    </> : t($_11 => $_11.copy)}
                            </Button>
                        </FlexRow>

                        {!hideFilterState && <Checkbox label={t($_12 => $_12.share_map_modal.include_map_filter_state)} checked={includeFilterState} onCheckedChange={details => setIncludeFilterState(details.checked === true)} variant="dark" size="md" id="include-map-filter-state" />}
                    </FlexColumn>
                </FlexColumn>
            </Modal>
            <MapEmbedModal isOpen={isEmbedModalOpen} onOpenChange={({
      open
    }) => setEmbedModalOpen(open)} />
        </>;
};